import {
  EntitlementSlugEnum,
  HRISProviderSlugEnum,
  PermissionEnum,
} from '@electricjs/core_entity-client';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import {
  EMBEDDED_ROUTES,
  INTERNAL_ROUTES,
  LEGACY_REDIRECT_PATHS,
  ROUTES,
} from '@/constants/routes';

import { useIntakeSurvey } from '@/hooks/useIntakeSurvey';
import { useTermsAgreement } from '@/hooks/useTermsAgreement';
import { useOrganizationHasEntitlement } from '@/hooks/useOrganizationHasEntitlement';
import { useAIChatFeatureFlags } from '@/hooks/useAIChatFeatureFlags';
import { useUserHasOrganizationPermission } from '@/hooks/useUserHasOrganizationPermission';
import AssetDetails from '@/pages/Assets/AssetDetails';
import Assets from '@/pages/Assets/Assets';
import NewAsset from '@/pages/Assets/NewAsset';
import NewHRISGroupChange from '@/pages/GroupChanges/NewHRISGroupChange';
import NewHRISOffboarding from '@/pages/Offboardings/NewHRISOffboarding';
import NewOffboarding from '@/pages/Offboardings/NewOffboarding';
import NewHRISOnboarding from '@/pages/Onboardings/NewHRISOnboarding';
import NewOnboarding from '@/pages/Onboardings/NewOnboarding';
import RequestSupportPage from '@/pages/SupportCenter/RequestSupportPage';
import SupportCenterHome from '@/pages/SupportCenter/SupportCenterHome';
import { useAppSelector } from '@/redux/store';
import { OrganizationUserRoleSlugs } from '@/types/users';

import PageSpinner from '@/components/PageSpinner';
import { RedirectWithSearchParams } from '@/components/RedirectWithSearchParams';
import { DefaultPageWrapper } from './DefaultPageWrapper';
import { ErrorPage } from './ErrorPage';
import { EmbeddedRoutes } from './embeddedRoutes';
import { InternalRoutes } from './internalRoutes';
import ProtectedRoute from './ProtectedRoute';
import { useOrganizationHasISVAddOns } from '@/hooks/useOrganizationHasISVAddOns';
import useIntegrationProvider from '@/components/Settings/IntegrationsPane/HRISIntegrations/useIntegrationProvider';
import { getIsProviderConnected } from '@/components/Settings/IntegrationsPane/IntegrationCard/helpers';
import useIsProdSupport from '@/hooks/useIsProdSupport';

// Page imports (lazy load)
const SecretSharePage = lazy(() => import('@/pages/Secrets/SecretSharePage'));
const PageNotFound = lazy(() => import('@/pages/PageNotFound'));
const MaintenancePage = lazy(() => import('@/pages/MaintenancePage'));
const IntakeForm = lazy(() => import('@/pages/IntakeForm/IntakeForm'));

// Dashboard
const Dashboard = lazy(() => import('@/pages/Dashboard/Dashboard'));

// Device related pages
const DeviceManagement = lazy(
  () => import('@/pages/Hardware/DeviceManagement/DeviceManagement')
);

// People related pages
const EmployeesPage = lazy(() => import('@/pages/People/EmployeesPage'));
const EmployeeGroups = lazy(
  () => import('@/pages/People/EmployeeGroups/EmployeeGroups')
);
const NewEmployeeGroup = lazy(
  () => import('@/pages/People/EmployeeGroups/NewEmployeeGroup')
);
const GroupDetails = lazy(
  () => import('@/pages/People/EmployeeGroups/GroupDetails')
);
const EmployeesStatusReview = lazy(
  () => import('@/pages/People/EmployeesStatusReview')
);
const EmployeeProfile = lazy(() => import('@/pages/People/EmployeeProfile'));
const HrisEmployeeProfile = lazy(
  () => import('@/pages/People/HrisEmployeeProfile')
);
const OrganizationApplications = lazy(
  () => import('@/pages/Applications/OrganizationApplications')
);
const ApplicationsDescriptionPage = lazy(
  () => import('@/pages/Applications/ApplicationsDescriptionPage')
);
const ApplicationDetails = lazy(
  () => import('@/pages/Applications/ApplicationDetails')
);

// Storefront/Orders related pages
const StorefrontPage = lazy(() => import('@/pages/Storefront/StorefrontPage'));
const OrderAndQuoteListPage = lazy(
  () => import('@/pages/Orders/OrderAndQuoteListPage')
);
const OrderDetailsPage = lazy(() => import('@/pages/Orders/OrderDetailsPage'));
const QuoteDetailsPage = lazy(() => import('@/pages/Orders/QuoteDetailsPage'));
const ShipLaptop = lazy(() => import('@/pages/Shipments/ShipLaptopPage'));
const QRCodeShipmentsList = lazy(
  () => import('@/pages/Shipments/QRCodeShipmentsPage')
);

// Organization related pages
const Settings = lazy(() => import('@/pages/Organization/Settings/Settings'));
const CompanySettings = lazy(
  () => import('@/pages/Organization/Settings/CompanySettings/CompanySettings')
);
const DevicePane = lazy(
  () => import('@/pages/Organization/Settings/DevicePane')
);
const BillingPane = lazy(
  () => import('@/pages/Organization/Settings/BillingPane')
);
const SupportCenterPane = lazy(
  () => import('@/pages/Organization/Settings/SupportCenterPane')
);
const SupportCenterPaneV2 = lazy(
  () => import('@/pages/Organization/Settings/SupportCenterPaneV2')
);
const IntegrationsPane = lazy(
  () => import('@/pages/Organization/Settings/IntegrationsPane')
);
const WebhooksPane = lazy(
  () => import('@/pages/Organization/Settings/WebhooksPane')
);
const WebhookDetailsPage = lazy(
  () => import('@/pages/Organization/Settings/WebhookDetailsPage')
);
const DivisionsPane = lazy(
  () => import('@/pages/Organization/Settings/DivisionsPane')
);
const DivisionDetails = lazy(
  () => import('@/pages/Organization/Settings/DivisionDetails')
);

const Discover = lazy(() => import('@/pages/Marketplace/Discover'));

// Employee center related pages
const AIChat = lazy(() => import('@/pages/AIChatCopilotKit'));
const HelpfulLinks = lazy(() => import('@/pages/HelpfulLinks/HelpfulLinks'));

const OrgSwitcher = lazy(() => import('@/pages/OrgSwitcher/OrgSwitcher'));

const NotificationCenter = lazy(
  () => import('@/pages/NotificationCenter/NotificationCenter')
);

const Plans = lazy(() => import('@/pages/Organization/Plans'));

// Security related pages
const SecurityDescriptionPage = lazy(
  () => import('@/pages/Security/SecurityDescriptionPage')
);
const Antivirus = lazy(
  () => import('@/pages/Security/ThreatDown/ThreatDownData')
);
const EmailSecurity = lazy(() => import('@/pages/Security/HEC/HECData'));
const BreachDashboard = lazy(
  () => import('@/pages/DataBreaches/BreachDashboard')
);

// tasks pages
const Tasks = lazy(() => import('@/pages/Tasks/Tasks'));
const TaskDetails = lazy(() => import('@/pages/Tasks/TaskDetails'));
const GroupChangeReview = lazy(
  () =>
    import(
      '@/components/Tasks/TaskDetails/RequestMappings/GroupChange/GroupChangeReview/GroupChangeReview'
    )
);

const SubResourcePickerPage = lazy(
  () =>
    import(
      '@/components/Tasks/TaskDetails/RequestMappings/GroupChange/GroupChangeReview/SubResourcePicker/SubResourcePickerPage'
    )
);

// Integrations pages
const IntegrationsConnect = lazy(
  () => import('@/pages/Integrations/IntegrationsConnect')
);
const HRISIntegrationsConnect = lazy(
  () => import('@/pages/Integrations/HRISIntegrationConnect')
);
const JustworksMigration = lazy(
  () => import('@/pages/Integrations/JustworksMigration')
);
const IntegrationsCallback = lazy(
  () => import('@/pages/Integrations/IntegrationsCallback')
);
const JustworksAuthPage = lazy(
  () => import('@/pages/Signup/Justworks/JustworksAuthPage')
);
const TrinetAuthPage = lazy(() => import('@/pages/Trinet/TrinetAuthPage'));
const ADPTermsPage = lazy(() => import('@/pages/ADP/ADPTermsPage'));

const GroupDetailsV2 = lazy(
  () => import('@/pages/People/EmployeeGroups/GroupDetailsV2')
);

const NewLeave = lazy(() => import('@/pages/Leave/NewLeave'));
const ReturnToWork = lazy(() => import('@/pages/Leave/ReturnToWork'));

export const ElectrolyteRoutes = () => {
  const loggedUser = useAppSelector(state => state.loggedUser);
  const allUserOrganizationRoles = loggedUser?.allUserOrganizationRoles;
  const hasEmployeeGroupsEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.EmployeeGroups
  );
  const hasAntivirusEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.ThreatdownSecurityDashboard
  );
  const hasEmailSecurityEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.HarmonyEmailAndCollaborationSecurityDashboard
  );
  const hasSupportCenterEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.SupportCenter
  );
  const hasXboardingsEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.Xboardings
  );
  const hasISVAddOns = useOrganizationHasISVAddOns();
  const hasHelpfulLinksEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.HelpfulLinks
  );
  const hasDirectHRISEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.DirectHrisIntegration
  );
  const hasApplicationsEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.Applications
  );
  const hasMdmEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.JumpcloudMdm
  );
  const hasWebhooksEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.Webhooks
  );
  const { provider } = useIntegrationProvider();

  const {
    itHubMaintenancePage, // maintenance page flag for when we have scheduled maintenance
    itHubMyOrganizationsSwitcher,
    ithTrinetPeoDirectIntegration,
    trinetPeoPartnerCredentials,
    groupsUiV2,
    itHubShipmentQrCodeHistory,
    ithFeatureDescriptionPages,
    removeItQuestionnaire,
    tasksUsabilityChanges,
    ithEmployeeLeave,
    ithOutboundWebhooks,
    ithDivisions,
  } = useFlags();

  const { hasAnyGigawattAccess } = useAIChatFeatureFlags();

  const hasElectricPermissions = !!loggedUser?.electricPermissions;
  const showOrganizationSwitcher =
    hasElectricPermissions ||
    (itHubMyOrganizationsSwitcher &&
      allUserOrganizationRoles !== null &&
      allUserOrganizationRoles !== undefined &&
      Object.keys(allUserOrganizationRoles)?.length > 1);
  const { shouldRenderIntakeForm } = useIntakeSurvey();
  const { shouldShowTermsAgreement, isTermsAgreementLoading } =
    useTermsAgreement();

  const isJustworksConnected = getIsProviderConnected(
    !hasDirectHRISEntitlement,
    provider?.slug,
    HRISProviderSlugEnum.Justworksdirect
  );

  const canReadAssets = useUserHasOrganizationPermission(
    PermissionEnum.Assetsread
  );
  const canCreateAssets = useUserHasOrganizationPermission(
    PermissionEnum.Assetscreate
  );
  const canUpdateAssets = useUserHasOrganizationPermission(
    PermissionEnum.Assetsupdate
  );
  const canCreateEmployees = useUserHasOrganizationPermission(
    PermissionEnum.Employeescreate
  );
  const canUpdateEmployees = useUserHasOrganizationPermission(
    PermissionEnum.Employeesupdate
  );
  const hasElectricAppEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.DesktopApp
  );
  const hasMarketplaceEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.Marketplace
  );
  const hasJustworksOnlyEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.JustworksOnly
  );
  const hasTrinetOnlyEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.TrinetOnly
  );
  const canReadShipments = useUserHasOrganizationPermission(
    PermissionEnum.Shipmentsread
  );
  const canReadOrganizationPackage = useUserHasOrganizationPermission(
    PermissionEnum.OrganizationPackageread
  );
  const canReadOrganizationAddOns = useUserHasOrganizationPermission(
    PermissionEnum.OrganizationAddOnsread
  );
  const canReadWebhooks = useUserHasOrganizationPermission(
    PermissionEnum.Webhooksread
  );

  const organizationId = loggedUser?.organizationId ?? '';
  const canViewBillingPane =
    canReadOrganizationPackage || canReadOrganizationAddOns;
  const canViewSupportCenterPane =
    hasXboardingsEntitlement || hasSupportCenterEntitlement;
  const canViewWebhooksPane =
    ithOutboundWebhooks && hasWebhooksEntitlement && canReadWebhooks;

  const isTrinetPEOConnected = getIsProviderConnected(
    ithTrinetPeoDirectIntegration
      ? !hasTrinetOnlyEntitlement
      : !hasDirectHRISEntitlement,
    provider?.slug,
    ithTrinetPeoDirectIntegration
      ? HRISProviderSlugEnum.TrinetPeodirect
      : HRISProviderSlugEnum.Trinet
  );

  const isTrinetHRPlatformConnected = getIsProviderConnected(
    !hasTrinetOnlyEntitlement,
    provider?.slug,
    HRISProviderSlugEnum.TrinetHrPlatform
  );

  const isTrinetConnected = isTrinetPEOConnected || isTrinetHRPlatformConnected;

  const isAdminOrSupportRole = loggedUser?.organizationUserRoles?.some(
    role =>
      role.slug === OrganizationUserRoleSlugs.SUPER_ADMIN ||
      role.slug === OrganizationUserRoleSlugs.PRODUCT_SUPPORT
  );

  const isProductSupport = useIsProdSupport();

  const maintenanceRoutes = (() => (
    <Routes>
      <Route path={ROUTES.MAINTENANCE} element={<MaintenancePage />} />
      <Route
        path={ROUTES.CATCH_ALL}
        element={<Navigate to={ROUTES.MAINTENANCE} />}
      />
    </Routes>
  ))();

  const intakeFormRoutes = (() => (
    <Routes>
      <Route
        path={ROUTES.WELCOME}
        element={<IntakeForm />}
        errorElement={<ErrorPage />}
      />
      <Route
        path={ROUTES.CATCH_ALL}
        element={<Navigate to={ROUTES.WELCOME} />}
      />
    </Routes>
  ))();

  const integrationRoutes = (() => (
    <Route>
      <Route
        path={ROUTES.INTEGRATIONS_CONNECT}
        element={<IntegrationsConnect />}
      />
      <Route
        path={ROUTES.INTEGRATIONS_HRIS_CONNECT}
        element={<HRISIntegrationsConnect />}
      />
      <Route
        path={ROUTES.INTEGRATIONS_OAUTH_CALLBACK}
        element={<IntegrationsCallback />}
      />
      <Route
        path={ROUTES.INTEGRATIONS_MIGRATION_JUSTWORKS}
        element={<JustworksMigration />}
      />
      <Route
        path={ROUTES.INTEGRATIONS_SETUP_JUSTWORKS}
        element={
          !hasJustworksOnlyEntitlement || isJustworksConnected ? (
            <Navigate to={ROUTES.HOME} replace />
          ) : (
            <JustworksAuthPage />
          )
        }
      />
      <Route element={<DefaultPageWrapper />}>
        <Route
          path={ROUTES.INTEGRATIONS_SETUP_TRINET}
          element={
            !hasTrinetOnlyEntitlement ||
            isTrinetConnected ||
            (trinetPeoPartnerCredentials && hasTrinetOnlyEntitlement) ? (
              <Navigate to={ROUTES.HOME} replace />
            ) : (
              <TrinetAuthPage />
            )
          }
        />
      </Route>
      <Route
        path={ROUTES.ADP_TERMS}
        element={
          !shouldShowTermsAgreement ? (
            <Navigate to={ROUTES.HOME} replace />
          ) : (
            <ADPTermsPage />
          )
        }
      />
    </Route>
  ))();

  const justworksSetupRoutes = (() => (
    <Routes>
      {integrationRoutes}
      <Route
        path={ROUTES.CATCH_ALL}
        element={<Navigate to={ROUTES.INTEGRATIONS_SETUP_JUSTWORKS} />}
      />
    </Routes>
  ))();

  const trinetSetupRoutes = (() => (
    <Routes>
      {integrationRoutes}
      <Route
        path={ROUTES.CATCH_ALL}
        element={<Navigate to={ROUTES.INTEGRATIONS_SETUP_TRINET} />}
      />
    </Routes>
  ))();

  const adpTermsRoutes = (() => (
    <Routes>
      {integrationRoutes}
      <Route
        path={ROUTES.CATCH_ALL}
        element={<Navigate to={ROUTES.ADP_TERMS} />}
      />
    </Routes>
  ))();

  const commonRoutes = (() => (
    <Route>
      <Route
        path={ROUTES.NOTIFICATION_CENTER}
        element={<NotificationCenter />}
      />
      {hasAnyGigawattAccess && (
        <Route path={ROUTES.AI_CHAT} element={<AIChat />} />
      )}
      {hasHelpfulLinksEntitlement && (
        <Route path={ROUTES.HELPFUL_LINKS} element={<HelpfulLinks />} />
      )}
      {(hasSupportCenterEntitlement || hasISVAddOns) && (
        <Route path={ROUTES.SUPPORT_CENTER} element={<SupportCenterHome />} />
      )}
      <Route path={ROUTES.REQUEST_SUPPORT} element={<RequestSupportPage />} />
      <Route path={ROUTES.TASKS} element={<Tasks />} />
      <Route path={ROUTES.TASKS_DETAIL} element={<TaskDetails />} />
      <Route
        path={ROUTES.TASKS_GROUP_CHANGE_REVIEW}
        element={<GroupChangeReview />}
      />
      <Route
        path={ROUTES.TASKS_GROUP_CHANGE_SUB_RESOURCES}
        element={<SubResourcePickerPage />}
      />

      {showOrganizationSwitcher && (
        <Route path={ROUTES.ORGANIZATION_SWITCHER} element={<OrgSwitcher />} />
      )}
      <Route path={ROUTES.SECRET_SHARE} element={<SecretSharePage />} />
    </Route>
  ))();

  const hubUserRoutes = (() => (
    <Route element={<DefaultPageWrapper />} errorElement={<ErrorPage />}>
      <Route>
        {(hasSupportCenterEntitlement || hasISVAddOns) && (
          <Route
            path={ROUTES.CATCH_ALL}
            element={<Navigate to={ROUTES.SUPPORT_CENTER} />}
          />
        )}
        {commonRoutes}
      </Route>
    </Route>
  ))();

  const appRoutes = (() => {
    // Homepage Rules
    // 1. If the user has one of the "provider-only" entitlements, and the integration is not setup, then the home route is the integration setup page.
    // 2. If the user is an admin or support role, then the home route is the dashboard.
    // 3. If the user is not an admin or support role, and the support center entitlement exists/ISV add-ons are enabled, then the home route is the support center.
    // 4. Otherwise, the home route is the tasks page.
    let homeRoute: string = ROUTES.TASKS;
    if (hasJustworksOnlyEntitlement && !isJustworksConnected) {
      homeRoute = ROUTES.INTEGRATIONS_SETUP_JUSTWORKS;
    } else if (
      hasTrinetOnlyEntitlement &&
      !isTrinetConnected &&
      !trinetPeoPartnerCredentials
    ) {
      homeRoute = ROUTES.INTEGRATIONS_SETUP_TRINET;
    } else if (
      !isAdminOrSupportRole &&
      (hasSupportCenterEntitlement || hasISVAddOns)
    ) {
      homeRoute = ROUTES.SUPPORT_CENTER;
    } else if (isAdminOrSupportRole) {
      homeRoute = ROUTES.DASHBOARD;
    }

    return (
      <Route>
        {integrationRoutes}
        <Route element={<DefaultPageWrapper />} errorElement={<ErrorPage />}>
          <Route>
            <Route
              path={ROUTES.WELCOME}
              element={<Navigate to={ROUTES.HOME} />}
            />
            <Route path={ROUTES.HOME} element={<Navigate to={homeRoute} />} />
            {commonRoutes}

            <Route path={ROUTES.DASHBOARD} element={<Dashboard />} />
            <Route path={ROUTES.PLANS} element={<Plans />} />
            {/* Security Pages */}
            {hasAntivirusEntitlement && (
              <Route
                path={ROUTES.SECURITY_SOLUTIONS_ANTIVIRUS}
                element={<Antivirus />}
              />
            )}
            {hasEmailSecurityEntitlement && (
              <Route
                path={ROUTES.SECURITY_SOLUTIONS_EMAIL_SECURITY}
                element={<EmailSecurity />}
              />
            )}
            <Route
              path={ROUTES.SECURITY}
              element={
                ithFeatureDescriptionPages && !hasMdmEntitlement ? (
                  <SecurityDescriptionPage />
                ) : (
                  <Navigate to={ROUTES.DEVICE_MANAGEMENT} replace />
                )
              }
            />
            <Route
              path={ROUTES.PASSWORD_BREACHES}
              element={<BreachDashboard />}
            />
            <Route path={ROUTES.EMPLOYEES} element={<EmployeesPage />} />
            <Route
              path={ROUTES.ACTIVE_EMPLOYEES}
              element={
                <RedirectWithSearchParams
                  to={ROUTES.EMPLOYEES}
                  params={{ tab: 'licensed' }}
                />
              }
            />
            <Route
              path={ROUTES.EMPLOYEES_STATUS_REVIEW}
              element={<EmployeesStatusReview />}
            />
            <Route
              path={ROUTES.EMPLOYEES_DETAIL}
              element={<EmployeeProfile />}
            />
            <Route
              path={ROUTES.EMPLOYEES_HRIS_DETAIL}
              element={<HrisEmployeeProfile />}
            />
            <Route
              path={ROUTES.INACTIVE_EMPLOYEES}
              element={
                <RedirectWithSearchParams
                  to={ROUTES.EMPLOYEES}
                  params={{ tab: 'unlicensed' }}
                />
              }
            />
            {hasEmployeeGroupsEntitlement && (
              <>
                <Route path={ROUTES.GROUPS} element={<EmployeeGroups />} />
                <Route
                  path={ROUTES.GROUPS_DETAIL}
                  element={groupsUiV2 ? <GroupDetailsV2 /> : <GroupDetails />}
                />
                <Route
                  path={ROUTES.GROUPS_NEW}
                  element={<NewEmployeeGroup />}
                />
              </>
            )}
            <Route
              path={ROUTES.APPLICATIONS}
              element={
                ithFeatureDescriptionPages &&
                !hasApplicationsEntitlement &&
                !isProductSupport ? (
                  <ApplicationsDescriptionPage />
                ) : (
                  <OrganizationApplications />
                )
              }
            />
            <>
              <Route
                path={ROUTES.ONBOARDINGS_HRIS_NEW}
                element={<NewHRISOnboarding />}
              />
              <Route
                path={ROUTES.OFFBOARDINGS_HRIS_NEW}
                element={<NewHRISOffboarding />}
              />
              <Route
                path={ROUTES.GROUP_CHANGES_HRIS_NEW}
                element={<NewHRISGroupChange />}
              />
            </>
            <Route
              path={ROUTES.APPLICATIONS_DETAIL}
              element={<ApplicationDetails />}
            />
            <Route
              path={LEGACY_REDIRECT_PATHS.HARDWARE} // For backwards compatibility
              element={<RedirectWithSearchParams to={ROUTES.GET_HARDWARE} />}
            />
            <Route
              path={LEGACY_REDIRECT_PATHS.PURCHASE_HARDWARE} // For backwards compatibility
              element={<RedirectWithSearchParams to={ROUTES.GET_HARDWARE} />}
            />
            <Route path={ROUTES.GET_HARDWARE} element={<StorefrontPage />} />
            <Route
              path={ROUTES.TRACK_ORDERS}
              element={<OrderAndQuoteListPage />}
            />
            <Route
              path={ROUTES.TRACK_ORDERS_DETAIL}
              element={<OrderDetailsPage />}
            />
            <Route path={ROUTES.QUOTE_DETAILS} element={<QuoteDetailsPage />} />
            <Route path={ROUTES.SHIP_LAPTOP} element={<ShipLaptop />} />
            {canReadShipments && itHubShipmentQrCodeHistory && (
              <Route
                path={ROUTES.SHIPMENTS_QR_CODE}
                element={<QRCodeShipmentsList />}
              />
            )}

            {/* Redirect for backwards compatibility in case anyone is still using the old URL */}
            <Route
              path={LEGACY_REDIRECT_PATHS.SHIP_DEVICE}
              element={<Navigate to={ROUTES.SHIP_LAPTOP} />}
            />
            <Route
              path={LEGACY_REDIRECT_PATHS.SCHEDULED_TASKS}
              element={
                <Navigate to={ROUTES.SETTINGS_COMPANY_PROFILE} replace />
              }
            />

            <Route path={ROUTES.SETTINGS} element={<Settings />}>
              <Route
                index
                element={
                  <Navigate to={ROUTES.SETTINGS_COMPANY_PROFILE} replace />
                }
              />
              <Route
                path="company-profile"
                element={
                  <Navigate to={ROUTES.SETTINGS_COMPANY_PROFILE} replace />
                }
              />
              <Route
                path="company"
                element={<CompanySettings organizationId={organizationId} />}
              />
              <Route path="device" element={<DevicePane />} />
              {canViewBillingPane && (
                <Route
                  path="billing"
                  element={<BillingPane orgId={organizationId} />}
                />
              )}
              {canViewSupportCenterPane && (
                <Route
                  path="category-champions"
                  element={
                    tasksUsabilityChanges ? (
                      <SupportCenterPaneV2 organizationId={organizationId} />
                    ) : (
                      <SupportCenterPane organizationId={organizationId} />
                    )
                  }
                />
              )}
              <Route path="integrations" element={<IntegrationsPane />} />
              {canViewWebhooksPane && (
                <>
                  <Route path="webhooks" element={<WebhooksPane />} />
                  <Route
                    path="webhooks/:webhookId"
                    element={<WebhookDetailsPage />}
                  />
                </>
              )}
              {ithDivisions && (
                <>
                  <Route path="divisions" element={<DivisionsPane />} />
                  <Route
                    path="divisions/:divisionId"
                    element={<DivisionDetails />}
                  />
                </>
              )}
            </Route>
            {hasMarketplaceEntitlement && (
              <Route path={ROUTES.DISCOVER} element={<Discover />} />
            )}

            {canReadAssets && hasElectricAppEntitlement && (
              <Route
                path={ROUTES.DEVICE_MANAGEMENT}
                element={<DeviceManagement />}
              />
            )}
            {canReadAssets && (
              <Route path={ROUTES.ASSETS} element={<Assets />} />
            )}
            {canCreateAssets && (
              <Route path={ROUTES.ASSETS_NEW} element={<NewAsset />} />
            )}
            {canReadAssets && (
              <Route path={ROUTES.ASSETS_DETAIL} element={<AssetDetails />} />
            )}
            {canUpdateAssets && (
              <Route
                path={ROUTES.ASSETS_EDIT}
                element={<AssetDetails isEditing />}
              />
            )}
            {canCreateEmployees && (
              <Route
                path={ROUTES.ONBOARDINGS_NEW}
                element={<NewOnboarding />}
              />
            )}
            {canUpdateEmployees && (
              <Route
                path={ROUTES.OFFBOARDINGS_NEW}
                element={<NewOffboarding />}
              />
            )}
            {ithEmployeeLeave && canUpdateEmployees && (
              <>
                <Route path={ROUTES.LEAVE_NEW} element={<NewLeave />} />
                <Route
                  path={ROUTES.LEAVE_RETURN_TO_WORK}
                  element={<ReturnToWork />}
                />
              </>
            )}
            <Route path={ROUTES.CATCH_ALL} element={<PageNotFound />} />
          </Route>
        </Route>
      </Route>
    );
  })();

  const reRoutes = (() => (
    <Route errorElement={<ErrorPage />}>
      {/* Security pages */}
      <Route
        path={LEGACY_REDIRECT_PATHS.SECURITY_SOLUTIONS}
        element={<Navigate replace to={ROUTES.DISCOVER} />}
      />
      <Route
        path={LEGACY_REDIRECT_PATHS.SECURITY_SOLUTIONS_THREATDOWN}
        element={<Navigate replace to={ROUTES.SECURITY_SOLUTIONS_ANTIVIRUS} />}
      />
      <Route
        path={LEGACY_REDIRECT_PATHS.SECURITY_SOLUTIONS_HEC}
        element={
          <Navigate replace to={ROUTES.SECURITY_SOLUTIONS_EMAIL_SECURITY} />
        }
      />
    </Route>
  ))();

  const getRoutesByRole = () => {
    // When the user is not Admin or Product Support then return Hub users roots
    if (!isAdminOrSupportRole) {
      return hubUserRoutes;
    }

    // Roots for Admin or Product Support users
    return (
      <>
        {appRoutes}
        {reRoutes}
      </>
    );
  };

  const routesToRender = (() => {
    if (itHubMaintenancePage) {
      return maintenanceRoutes;
    }

    if (shouldRenderIntakeForm && !removeItQuestionnaire) {
      return intakeFormRoutes;
    }

    if (hasJustworksOnlyEntitlement && !isJustworksConnected) {
      return justworksSetupRoutes;
    }

    if (
      hasTrinetOnlyEntitlement &&
      !isTrinetConnected &&
      !trinetPeoPartnerCredentials
    ) {
      return trinetSetupRoutes;
    }

    if (shouldShowTermsAgreement) {
      return adpTermsRoutes;
    }

    if (isTermsAgreementLoading) {
      return <PageSpinner />;
    }

    return (
      <Routes>
        {/* Embedded routes without navigation for iframe integration */}
        {/* Feature flag determines if routes exist; EmbedBlocker handles iframe validation */}
        {/* IMPORTANT: These routes must be before getRoutesByRole() to avoid the catch-all 404 */}
        <Route path={EMBEDDED_ROUTES.WILDCARD} element={<EmbeddedRoutes />} />
        <Route element={<ProtectedRoute isAllowed={hasElectricPermissions} />}>
          <Route path={INTERNAL_ROUTES.WILDCARD} element={<InternalRoutes />} />
        </Route>
        {getRoutesByRole()}
      </Routes>
    );
  })();

  return routesToRender;
};

export default ElectrolyteRoutes;
